import React from "react";
import { Box, Typography, Grid, Paper, Divider, List, ListItem, ListItemIcon, ListItemText, alpha, Button } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SectionContainer from "../ui/SectionContainer";
import { useLanguage } from "../../i18n/LanguageContext";
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import ComputerIcon from '@mui/icons-material/Computer';
import LanguageIcon from '@mui/icons-material/Language';

const Pack = ({ hideHeader = false }) => {
  const { t } = useLanguage();

  const packsData = [
    {
      id: 1,
      title: t('packs.startup'),
      icon: <RocketLaunchIcon sx={{ fontSize: 45 }} />,
      color: '#2196F3',
      features: t('packs.features.startup'),
      popular: false
    },
    {
      id: 2,
      title: t('packs.growth'),
      icon: <TrendingUpIcon sx={{ fontSize: 45 }} />,
      color: '#DD8748',
      features: t('packs.features.growth'),
      popular: true
    },
    {
      id: 3,
      title: t('packs.pro'),
      icon: <WorkspacePremiumIcon sx={{ fontSize: 45 }} />,
      color: '#1D2558',
      features: t('packs.features.pro'),
      popular: false
    },
    {
      id: 4,
      title: t('packs.commercial'),
      icon: <CorporateFareIcon sx={{ fontSize: 45 }} />,
      color: '#673ab7',
      features: [t('packs.features.startup.0'), t('packs.features.startup.1')],
      popular: false
    },
    {
      id: 5,
      title: t('packs.desktop'),
      icon: <ComputerIcon sx={{ fontSize: 45 }} />,
      color: '#009688',
      features: [t('packs.features.startup.2'), t('packs.features.startup.3')],
      popular: false
    },
    {
      id: 6,
      title: t('packs.web'),
      icon: <LanguageIcon sx={{ fontSize: 45 }} />,
      color: '#e91e63',
      features: [t('common.learnMore')],
      popular: false
    }
  ];

  return (
    <SectionContainer bgColor="#ffffff" py={12}>
      {!hideHeader && (
        <Box textAlign="center" mb={12} data-aos="fade-down">
          <Typography
            variant="overline"
            sx={{ color: '#DD8748', fontWeight: 900, letterSpacing: 4, mb: 2, fontSize: '1.3rem'}}
          >
            {t('packs.title')}
          </Typography>
          <Typography
            variant="h2"
            sx={{ fontWeight: 950, color: '#1D2558', mt: 1, letterSpacing: -1.5 }}
          >
            {t('packs.subtitle')}
          </Typography>
        </Box>
      )}

      <Grid container spacing={4} alignItems="stretch">
        {packsData.map((val) => (
          <Grid item xs={12} sm={6} md={4} key={val.id} data-aos="fade-up" data-aos-delay={val.id * 100}>
            <Paper
              elevation={0}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 8,
                overflow: 'hidden',
                border: '1px solid rgba(0,0,0,0.06)',
                bgcolor: val.popular ? '#fff' : alpha('#fbfcfd', 0.8),
                transition: 'all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                position: 'relative',
                '&:hover': {
                  bgcolor: '#fff',
                  transform: 'translateY(-15px)',
                  boxShadow: `0 40px 80px ${alpha(val.color, 0.12)}`,
                  borderColor: val.color,
                  '& .icon-box': { bgcolor: val.color, color: '#fff' },
                  '& .btn-pack': { bgcolor: val.color, color: '#fff' }
                }
              }}
            >
              {val.popular && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 20,
                    right: -35,
                    bgcolor: '#DD8748',
                    color: '#fff',
                    px: 5,
                    py: 0.8,
                    transform: 'rotate(45deg)',
                    fontWeight: 900,
                    fontSize: '0.75rem',
                    boxShadow: '0 5px 15px rgba(248, 158, 52, 0.3)'
                  }}
                >
                  POPULAR
                </Box>
              )}

              <Box sx={{ p: 5, textAlign: 'center' }}>
                <Box
                  className="icon-box"
                  sx={{
                    width: 90,
                    height: 90,
                    borderRadius: '30px',
                    bgcolor: alpha(val.color, 0.1),
                    color: val.color,
                    mx: 'auto',
                    mb: 4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.4s ease'
                  }}
                >
                  {val.icon}
                </Box>
                <Typography variant="h4" fontWeight={900} color="#1D2558" sx={{ mb: 2 }}>
                  {val.title}
                </Typography>

                <Divider sx={{ mb: 4, width: 40, mx: 'auto', bgcolor: val.color, height: 4, borderRadius: 2 }} />

                <List sx={{ minHeight: 200 }}>
                  {val.features && val.features.map((feature, i) => (
                    <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start', py: 1.2 }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.4 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 20, color: val.color }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', fontWeight: 600, lineHeight: 1.5 }}
                      />
                    </ListItem>
                  ))}
                </List>

                <Button
                  variant="outlined"
                  fullWidth
                  className="btn-pack"
                  sx={{
                    mt: 4,
                    py: 1.8,
                    borderRadius: 4,
                    borderColor: alpha(val.color, 0.3),
                    color: val.color,
                    fontWeight: 900,
                    transition: '0.4s ease'
                  }}
                >
                  {t('common.learnMore')}
                </Button>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </SectionContainer>
  );
};

export default Pack;